import { getSerialized } from '@extension/wa-communication-layer/core/utils/wa-key-serialization';
import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { CommonRootState } from "@common/types/common-root-state-type";

// import { AnalyticsService } from "@common/services/analytics-service";
import { User } from "@common-models/user";
import { UserMessage } from '@bt-common/schemas/bt/user-message';
import { MessageState } from "@common/types/interface";
import { Poll } from "@extension/context/content/components/add-scheduled-message-button/schedule-message-types";
import { Chat } from "@bt-common/interfaces/whatsapp-types";
import { ExtendedUserMessage } from "../models/extend-user-message";
import { applyAddRecipient, selectStatusOnly } from "../scheduling/status-recipient";
import { normalizeScheduledDueDate } from "../utils/scheduled-due-date";
import { newClientIdempotencyKey } from "../utils/client-idempotency-key";

/** One selected recipient (contact / group / channel). Same shape as a
 *  UserMessage.contactList entry, so it flows straight into the persist path. */
export type ScheduleRecipient = NonNullable<UserMessage['contactList']>[number];

/** The single derivation point for the recipient list (FE#230). Drops entries
 *  with an empty/whitespace id (they can never be sent) and dedupes by id, so a
 *  duplicate add or a dirty seed can never inflate or corrupt the count. */
export function sanitizeRecipients(list: readonly ScheduleRecipient[] | undefined): ScheduleRecipient[] {
  const seen = new Set<string>();
  const out: ScheduleRecipient[] = [];
  for (const r of list ?? []) {
    const id = r?.id != null ? String(r.id).trim() : '';
    if (!id || seen.has(id)) continue;
    seen.add(id);
    out.push(r);
  }
  return out;
}

/** Map the active WhatsApp chat to a recipient (used only as an open-time
 *  fallback when the incoming message carries no contactList — e.g. scheduling
 *  straight from an open chat). Returns null for a chat with no usable id. */
export function recipientFromChat(chat: Chat | undefined | null): ScheduleRecipient | null {
  const id = getSerialized(chat?.id);
  if (!id || !String(id).trim()) return null;
  const anyChat = chat as any;
  const name = anyChat.name || anyChat.displayName || anyChat.contact?.name;
  return { id, name, displayName: anyChat.contact?.name || anyChat.displayName } as ScheduleRecipient;
}

/** Resolve the recipient list to seed ON OPEN: the message's own contactList if
 *  it has one, else the active chat, else empty. Sanitized (deduped, no empty id). */
export function deriveOpenRecipients(
  contactList: readonly ScheduleRecipient[] | undefined,
  chat: Chat | undefined | null,
): ScheduleRecipient[] {
  const fromList = sanitizeRecipients(contactList);
  if (fromList.length > 0) return fromList;
  const fromChat = recipientFromChat(chat);
  return fromChat ? [fromChat] : [];
}

export interface AddScheduledMessageButtonState {
  showErrorDialog: boolean;
  open: boolean;
  message: Partial<UserMessage> & Omit<UserMessage, '_id'> & { _id?: string };
  messageDraft: Partial<UserMessage> & Omit<UserMessage, '_id'> & { _id?: string; dueDateSetByUser?: boolean }; // Working copy to prevent race conditions (BE#657 adds the UI-only dueDateSetByUser)
  /** FE#230: the authoritative recipient selection. Single source of truth —
   *  written ONLY by addRecipient/removeRecipient and seeded once on open. A
   *  message-body edit or a wholesale message re-init cannot touch it, so an
   *  active multi-recipient selection can no longer be clobbered back to a
   *  single (placeholder) entry. Persist reads `selectScheduleRecipients`. */
  recipients: ScheduleRecipient[];
  isEmojiPicker: boolean;
  user?: User | null | undefined;
  currentChat?: Chat;
  showConfirmationDialog?: boolean;
  originalMessage?: UserMessage;
  pollFromWAModal?: Poll;
  linkPreviewLoading: boolean;
  isWaSocketsLimitReached: boolean | null;
  openedFromContext?: 'calendar-sidebar' | 'default'; // Track where popup was opened from
  lastClosedAt: number | null;
}

const initialState: AddScheduledMessageButtonState = {
  showErrorDialog: false,
  open: false,
  message: {
    dueDate: undefined,
    message: "",
    isRecurring: false,
    state: MessageState.pending,
    cancelIfReceived: false,
  },
  messageDraft: {
    dueDate: undefined,
    message: "",
    isRecurring: false,
    state: MessageState.pending,
    cancelIfReceived: false,
  },
  isEmojiPicker: false,
  showConfirmationDialog: false,
  originalMessage: undefined,
  isWaSocketsLimitReached: null,
  pollFromWAModal: undefined,
  linkPreviewLoading: false,
  openedFromContext: 'default',
  lastClosedAt: null,
  recipients: [],
};


const AddScheduledMessageButtonSlice = createSlice({
  name: "AddScheduledMessageButtonSlice",
  initialState,
  reducers: {
    editMessage: (state, action: PayloadAction<ExtendedUserMessage>) => {
      const now = Date.now();
      if (state.lastClosedAt && now - state.lastClosedAt < 250) {
        console.log('[Schedule Guard] Ignoring reopen attempt shortly after close (editMessage).');
        return;
      }
      
      const message = { ...action.payload };
      if (!state.open) {
        // BE#657 — normalize an EXISTING dueDate (string → Date) but never fabricate
        // "now" for a date-less new draft: a near-now dueDate arms an immediate send
        // before the user picks the intended future date.
        message.dueDate = normalizeScheduledDueDate(message.dueDate);
        message.state = MessageState.editing;
        state.lastClosedAt = null;
        state.open = true;
        state.message = message;
        state.messageDraft = { ...message }; // Initialize draft with same data
        // BE#657 — only an EXISTING message (has _id) with a stored dueDate counts as a
        // prior explicit user choice. A NEW draft is false regardless of any seeded date,
        // so Send stays disabled until the user actually picks a time.
        state.messageDraft.dueDateSetByUser = !!(message as { dueDateSetByUser?: boolean }).dueDateSetByUser || !!(message._id && message.dueDate);
        // FE#230: seed the authoritative recipient list ONCE on open (from the
        // message being edited / the chat we opened from). Guarded by !state.open,
        // so it cannot re-fire and clobber an active selection mid-session.
        state.recipients = deriveOpenRecipients(message.contactList, state.currentChat);
        // BE#372/#231 — mint a fresh idempotency key at dialog-OPEN (not lazily on save), so a
        // double-click's two clicks both read the SAME draft key and the backend dedupes the second
        // create. A fresh key per open session also means we never inherit the ORIGINAL's stored key
        // (which would make the backend dedupe hook return the original instead of the edited copy).
        state.messageDraft.clientIdempotencyKey = newClientIdempotencyKey();
        state.openedFromContext = 'default'; // Default context
      }
    },
    editMessageFromContext: (state, action: PayloadAction<{ message: UserMessage, context: 'calendar-sidebar' | 'default' }>) => {
      const now = Date.now();
      if (state.lastClosedAt && now - state.lastClosedAt < 250) {
        console.log('[Schedule Guard] Ignoring reopen attempt shortly after close (editMessageFromContext).');
        return;
      }
      const { message, context } = action.payload;
      const messageToEdit = { ...message };
      if (!state.open) {
        // BE#657 — see editMessage: normalize an existing date, never seed "now".
        messageToEdit.dueDate = normalizeScheduledDueDate(messageToEdit.dueDate);
        messageToEdit.state = MessageState.editing;
        state.lastClosedAt = null;
        state.open = true;
        state.message = messageToEdit;
        state.messageDraft = { ...messageToEdit }; // Initialize draft with same data
        // BE#657 — see editMessage: user-set only for an existing message with a date.
        state.messageDraft.dueDateSetByUser = !!(messageToEdit as { dueDateSetByUser?: boolean }).dueDateSetByUser || !!(messageToEdit._id && messageToEdit.dueDate);
        state.recipients = deriveOpenRecipients(messageToEdit.contactList, state.currentChat); // FE#230: seed once on open
        state.messageDraft.clientIdempotencyKey = newClientIdempotencyKey(); // fresh key at open (see editMessage)
        state.openedFromContext = context;
      }
    },
    updateMessage: (state, action: PayloadAction<ExtendedUserMessage>) => {
      state.message = action.payload;
    },
    // Simple, efficient field merging
    mergeMessage: (state, action: PayloadAction<Partial<UserMessage>>) => {
      // Simple merge - the message editor handles debouncing, so this can be straightforward
      state.message = {
        ...state.message,
        ...action.payload
      };
    },
    // New action for updating the draft (used by wrapper to avoid race conditions)
    updateMessageDraft: (state, action: PayloadAction<Partial<UserMessage> & { dueDateSetByUser?: boolean }>) => {
      state.messageDraft = {
        ...state.messageDraft,
        ...action.payload
      };
    },
    closeDialog: (state, action: PayloadAction<void>) => {
      return {
        ...initialState,
        lastClosedAt: Date.now(),
      };
    },
    setIsEmojiPicker: (state, action: PayloadAction<boolean>) => {
      state.isEmojiPicker = action.payload;
    },
    editCalendarMessage: (state, action: PayloadAction<ExtendedUserMessage>) => {
      const now = Date.now();
      if (state.lastClosedAt && now - state.lastClosedAt < 250) {
        console.log('[Schedule Guard] Ignoring reopen attempt shortly after close (editCalendarMessage).');
        return;
      }
      
      const message = { ...action.payload };

      // FE#230: this action is deliberately NOT `!state.open`-guarded, so it can
      // re-fire while the dialog is already open. Capture the prior open-state so
      // the recipient seed happens ONLY on a real closed→open transition — a
      // re-fire mid-selection must never clobber the active recipient list.
      const wasOpen = state.open;
      message.state = MessageState.editing;
      state.lastClosedAt = null;
      state.open = true;
      state.message = message;
      state.messageDraft = { ...message }; // Initialize draft with same data
      // BE#657 — user-set only for an existing message with a date (see editMessage).
      state.messageDraft.dueDateSetByUser = !!(message as { dueDateSetByUser?: boolean }).dueDateSetByUser || !!(message._id && message.dueDate);
      if (!wasOpen) {
        state.recipients = deriveOpenRecipients(message.contactList, state.currentChat);
      }
      state.messageDraft.clientIdempotencyKey = newClientIdempotencyKey(); // fresh key at open (see editMessage)
      state.originalMessage = { ...message }
      state.openedFromContext = 'default'; // Default context
    },
    setConfirmationDialog: (state, action: PayloadAction<boolean>) => {
      state.showConfirmationDialog = action.payload;
    },
    setPollFromWAModal: (state, action: PayloadAction<Poll | undefined>) => {
      state.pollFromWAModal = action.payload;
    },
    setLinkPreviewLoading: (state, action: PayloadAction<boolean>) => {
      state.linkPreviewLoading = action.payload;
    },
    // FE#230 — the ONLY writers of the recipient selection from the picker.
    // Surgical add/remove keep the multi-recipient list intact; a message-body
    // edit or a wholesale message re-init cannot reach `recipients`.
    addRecipient: (state, action: PayloadAction<ScheduleRecipient>) => {
      const id = action.payload?.id != null ? String(action.payload.id).trim() : '';
      if (!id) return; // an empty/whitespace id can never be sent — ignore it
      // FE#335 — apply Status↔chat exclusivity (adding Status replaces all; adding a
      // normal contact drops any Status entry first). Preserves the FE#230 dedupe-by-id.
      state.recipients = applyAddRecipient(state.recipients, action.payload);
    },
    removeRecipient: (state, action: PayloadAction<string>) => {
      const id = String(action.payload ?? '').trim();
      state.recipients = state.recipients.filter((r) => String(r?.id ?? '').trim() !== id);
    },
    // FE#335 — pick "Schedule a Status": the Status recipient replaces any selection.
    setStatusRecipient: (state) => {
      state.recipients = selectStatusOnly();
    },
    setRecipients: (state, action: PayloadAction<ScheduleRecipient[]>) => {
      state.recipients = sanitizeRecipients(action.payload);
    },
  },
});

const selectState = (state: CommonRootState) => state.AddScheduledMessageButtonReducer
export const selectIsOpen = createSelector(selectState, (s: AddScheduledMessageButtonState) => s.open);

/** FE#230 — the single, memoized derivation of the recipient selection. Both the
 *  picker (display) and the footer (persist) read THIS. Re-sanitizes on read so a
 *  dirty seed can never surface a duplicate or an unsendable empty-id recipient. */
export const selectScheduleRecipients = createSelector(
  selectState,
  (s: AddScheduledMessageButtonState) => sanitizeRecipients(s.recipients),
);


export const addScheduledMessageButtonActions =
  AddScheduledMessageButtonSlice.actions;
export default AddScheduledMessageButtonSlice.reducer;
